import _to_consumable_array from "@swc/helpers/src/_to_consumable_array.mjs";
import { fromEventPattern as observableFromEventPattern, of as observableOf, merge as observableMerge, from as observableFrom } from "rxjs";
import { delay, switchMap, ignoreElements, filter, map, mergeMap, tap } from "rxjs/operators";
// eslint-disable-next-line no-restricted-imports
import i18next from "i18next";
import { ZENDESK_PUBLIC_ID } from "~/constants/index";
import { I18N_ZENDESK_LOCALES } from "~/services/translation/constants";
import { userSelectors } from "~/services/user";
import { noop } from "~/services/utils";
import { combineEpicsWithCatches } from "~/store";
import theme from "~/styles/theme";
import { actions, actionTypes } from "./zendesk.ducks";
var loadZendesk = function() {
    return new Promise(function(resolve) {
        var existingScript = document.getElementById("ze-snippet");
        if (!existingScript) {
            var script = document.createElement("script");
            script.nonce = window.SCRIPT_NONCE;
            script.id = "ze-snippet";
            script.onload = function() {
                // ensure that window.zE is actually present before we resolve
                if (window.zE) {
                    resolve();
                }
            };
            script.src = "https://static.zdassets.com/ekr/snippet.js?key=".concat(ZENDESK_PUBLIC_ID);
            document.body.appendChild(script);
        }
        // ensure that window.zE is actually present before we resolve
        if (window.zE) {
            resolve();
        }
    });
};
var loadZendeskScript = function(action$) {
    return action$.ofType(actionTypes.ZENDESK_SCRIPT_LOAD).pipe(tap(function() {
        // These settings are used when the widget is loaded. Need to use the window.zE()
        // API to make updates after it has loaded.
        window.zESettings = {
            webWidget: {
                chat: {
                    // Hide the departments selection
                    departments: {
                        enabled: []
                    }
                },
                // Note that the button has 16x24 px padding, so 0, 0 isn't actually in the
                // absolute bottom corner
                offset: {
                    horizontal: "0px",
                    vertical: "0px"
                },
                zIndex: theme.zIndex.zendeskWidget
            }
        };
    }), mergeMap(function() {
        return observableFrom(loadZendesk()).pipe(map(actions.zendeskScriptLoaded));
    }));
};
var addUserToZendesk = function(action$, store) {
    return action$.ofType(actionTypes.ZENDESK_SCRIPT_LOADED).pipe(map(function() {
        return userSelectors.getEmail(store.value);
    }), filter(function(email) {
        return email;
    }), tap(function(email) {
        if (window.zE) {
            window.zE("webWidget", "identify", {
                email: email
            });
            window.zE("webWidget", "prefill", {
                email: {
                    value: email,
                    readOnly: true
                }
            });
        }
    }), ignoreElements());
};
var setZendeskLanguage = function(action$) {
    return action$.ofType(actionTypes.ZENDESK_SCRIPT_LOADED).pipe(switchMap(function() {
        return observableMerge(// Set language on load, and listen for changes
        observableOf(1), observableFromEventPattern(function(handler) {
            return i18next.on("languageChanged", handler);
        }, function(handler) {
            return i18next.off("languageChanged", handler);
        })).pipe(tap(function() {
            if (i18next.language && window.zE) {
                window.zE("webWidget", "setLocale", I18N_ZENDESK_LOCALES[i18next.language]);
            }
        }), ignoreElements());
    }));
};
function zendeskEventFactory(eventName) {
    var zE = window.zE || noop;
    var hasRemovedListener = false;
    return observableFromEventPattern(function(handler) {
        zE("webWidget:on", eventName, function() {
            for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                args[_key] = arguments[_key];
            }
            return !hasRemovedListener && handler.apply(void 0, _to_consumable_array(args));
        });
    }, function() {
        hasRemovedListener = true;
    });
}
var addZendeskListeners = function(action$) {
    return action$.ofType(actionTypes.ZENDESK_SCRIPT_LOADED).pipe(switchMap(function() {
        return observableMerge(zendeskEventFactory("open").pipe(map(function() {
            return actions.widgetOpened();
        })), zendeskEventFactory("close").pipe(map(function() {
            return actions.widgetClosed();
        })));
    }));
};
// The Zendesk widget isn't actually visible until about 1-2 secs after the script has loaded.
// Let's debounce the event so loading states are maintained for a little longer even after
// the script has loaded.
var debounceZendeskLoadEvent = function(action$) {
    return action$.ofType(actionTypes.ZENDESK_SCRIPT_LOADED).pipe(delay(1000), map(function() {
        return actions.zendeskScriptLoadedAndDelayed();
    }));
};
export default combineEpicsWithCatches(loadZendeskScript, addUserToZendesk, setZendeskLanguage, addZendeskListeners, debounceZendeskLoadEvent);
